// Google IMA HTML5 SDK integration for a single on-demand midroll ad break,
// with a client-side ad waterfall (HilltopAds primary → ExoClick backup).
// Keeps the existing content <video>; renders ads into an overlay container,
// pauses content only when an ad is actually ready, resumes on
// complete/skip/error/no-fill. Never plays more than one ad per trigger and
// never leaves the episode stuck (8–10s protection).

const IMA_SRC = "https://imasdk.googleapis.com/js/sdkloader/ima3.js";
let imaPromise = null;

export function loadIma() {
  if (typeof window !== "undefined" && window.google && window.google.ima) return Promise.resolve();
  if (imaPromise) return imaPromise;
  imaPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = IMA_SRC;
    s.async = true;
    s.onload = () => {
      if (window.google && window.google.ima) {
        console.info("[DAKID ads] IMA SDK loaded OK");
        resolve();
      } else {
        console.warn("[DAKID ads] IMA SDK script loaded but google.ima missing");
        reject(new Error("ima missing"));
      }
    };
    s.onerror = () => {
      console.warn("[DAKID ads] IMA SDK FAILED to load from imasdk.googleapis.com — this is almost always network/DNS ad-blocking on this connection (Wi-Fi router ad-block, ISP filter, or a device DNS blocker like Pi-hole/AdGuard/NextDNS, or a browser content blocker). No ad can play; content continues normally.");
      reject(new Error("ima load failed"));
    };
    document.head.appendChild(s);
  });
  return imaPromise;
}

const rawVastTag = () => {
  try {
    const o = localStorage.getItem("dakid_vast_tag");
    if (o) return o;
  } catch {}
  return process.env.REACT_APP_VAST_TAG_URL || "";
};

// Ad waterfall tiers, in order. Tier 1 = HilltopAds (primary), Tier 2 = ExoClick
// (backup). Loaded DIRECTLY client-side so each network sees the real viewer.
export const getVastTags = () => {
  const tags = [];
  const t1 = rawVastTag();
  if (t1) tags.push(t1);
  const t2 = process.env.REACT_APP_VAST_TAG_URL_2 || "";
  if (t2) tags.push(t2);
  return tags;
};

// Back-compat single-tag helper.
export const getVastTagUrl = () => rawVastTag();

const TIER_LABELS = ["HilltopAds (primary)", "ExoClick (backup)"];

// onAdStart(): ad break began (content should be visually covered)
// onResume(): ad finished / skipped / errored / no-fill (resume content)
export function createAdController({ videoEl, adContainerEl, tagUrls, onAdStart, onResume }) {
  const ima = () => window.google.ima;
  const tags = Array.isArray(tagUrls) ? tagUrls.filter(Boolean) : (tagUrls ? [tagUrls] : []);

  let adDisplayContainer = null;
  let adsLoader = null;
  let adsManager = null;
  let initialized = false;

  let done = false;          // ad break finished (content resumed / no more tiers)
  let adStarted = false;     // an ad actually began rendering → lock in, no more tiers
  let contentPaused = false; // content has been paused for this break
  let currentTier = -1;

  let tierTimer = null;      // per-tier: advance if a provider never responds
  let overallTimer = null;   // stuck-protection: resume ~10s after content paused

  const clearTierTimer = () => { if (tierTimer) { clearTimeout(tierTimer); tierTimer = null; } };
  const clearOverall = () => { if (overallTimer) { clearTimeout(overallTimer); overallTimer = null; } };

  const destroyCurrent = () => {
    try { adsManager && adsManager.destroy(); } catch {}
    adsManager = null;
    try { adsLoader && adsLoader.destroy && adsLoader.destroy(); } catch {}
    adsLoader = null;
  };

  // End the ad break and resume the episode (once).
  const resume = () => {
    if (done) return;
    done = true;
    clearTierTimer();
    clearOverall();
    destroyCurrent();
    try { onResume && onResume(); } catch {}
  };

  const ensureContainer = () => {
    if (!adDisplayContainer) adDisplayContainer = new (ima().AdDisplayContainer)(adContainerEl, videoEl);
  };

  // Must run inside a user gesture (called on content play) so ads can play.
  const initOnGesture = () => {
    try {
      ensureContainer();
      if (!initialized) { adDisplayContainer.initialize(); initialized = true; }
    } catch {}
  };

  // Pause the episode + cover it. Only happens when an ad is actually ready.
  const pauseContent = () => {
    if (contentPaused) return;
    contentPaused = true;
    try { videoEl.pause(); } catch {}
    try { onAdStart && onAdStart(); } catch {}
    // Stuck-protection: if no ad has actually rendered within 10s of pausing,
    // resume the episode no matter what tier we're on.
    clearOverall();
    overallTimer = setTimeout(() => {
      if (done || adStarted) return;
      console.warn("[DAKID ads] no ad rendered within 10s of the ad break — resuming the episode (all providers blocked/failed on this network).");
      resume();
    }, 10000);
  };

  const onAdError = (e) => {
    let code, vast, msg;
    try {
      const err = e && e.getError && e.getError();
      if (err) {
        code = err.getErrorCode && err.getErrorCode();
        vast = err.getVastErrorCode && err.getVastErrorCode();
        msg = err.getMessage && err.getMessage();
      }
    } catch {}
    const label = TIER_LABELS[currentTier] || `tier ${currentTier + 1}`;
    console.warn(`[DAKID ads] ${label} failed — code:`, code, "vastCode:", vast, "msg:", msg);
    if (adStarted) return resume();   // an ad already played → do not waterfall
    requestTier(currentTier + 1);     // try the next provider
  };

  const onManagerLoaded = (e, i) => {
    if (done || i !== currentTier) { try { e.getAdsManager(videoEl).destroy(); } catch {} return; }
    clearTierTimer();
    try {
      const label = TIER_LABELS[i] || `tier ${i + 1}`;
      console.info(`[DAKID ads] ${label} filled — starting ad`);
      const settings = new (ima().AdsRenderingSettings)();
      settings.restoreCustomPlaybackStateOnAdBreakComplete = true;
      // IMA's VPAID creative-load timeout defaults to -1 (infinite). A finite 8s
      // makes IMA abandon a blocked/slow creative and fire AD_ERROR, which lets us
      // waterfall to the next provider (or resume) instead of hanging.
      try { settings.loadVideoTimeout = 8000; } catch {}
      adsManager = e.getAdsManager(videoEl, settings);

      adsManager.addEventListener(ima().AdErrorEvent.Type.AD_ERROR, onAdError);
      adsManager.addEventListener(ima().AdEvent.Type.CONTENT_PAUSE_REQUESTED, pauseContent);
      adsManager.addEventListener(ima().AdEvent.Type.STARTED, () => {
        adStarted = true;      // an ad is really playing → lock this tier in
        clearOverall();
      });
      adsManager.addEventListener(ima().AdEvent.Type.CONTENT_RESUME_REQUESTED, resume);
      adsManager.addEventListener(ima().AdEvent.Type.ALL_ADS_COMPLETED, resume);

      const w = videoEl.clientWidth || 640;
      const h = videoEl.clientHeight || 360;
      adsManager.init(w, h, ima().ViewMode.NORMAL);
      adsManager.start();
    } catch {
      requestTier(i + 1);
    }
  };

  // Request tier `i` of the waterfall. Content is NOT paused here.
  const requestTier = (i) => {
    if (done) return;
    // Tear down the previous tier's loader/manager, but keep the episode paused
    // if it already is (so the next tier can render seamlessly).
    destroyCurrent();
    clearTierTimer();

    if (i >= tags.length) {
      console.info("[DAKID ads] all ad providers failed/blocked — resuming episode.");
      return resume();
    }
    currentTier = i;
    const label = TIER_LABELS[i] || `tier ${i + 1}`;
    try {
      ensureContainer();
      if (!initialized) { adDisplayContainer.initialize(); initialized = true; }

      adsLoader = new (ima().AdsLoader)(adDisplayContainer);
      adsLoader.addEventListener(ima().AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, (e) => onManagerLoaded(e, i), false);
      adsLoader.addEventListener(ima().AdErrorEvent.Type.AD_ERROR, onAdError, false);

      const req = new (ima().AdsRequest)();
      req.adTagUrl = tags[i];
      const w = videoEl.clientWidth || 640;
      const h = videoEl.clientHeight || 360;
      req.linearAdSlotWidth = w;
      req.linearAdSlotHeight = h;
      req.nonLinearAdSlotWidth = w;
      req.nonLinearAdSlotHeight = Math.round(h / 3);
      if (req.setAdWillAutoPlay) req.setAdWillAutoPlay(true);
      if (req.setAdWillPlayMuted) req.setAdWillPlayMuted(false);
      console.info(`[DAKID ads] requesting ad from ${label}:`, tags[i]);
      adsLoader.requestAds(req);

      // Per-tier watchdog: if this provider neither fills nor errors within 8s
      // (request dropped/blocked), move on to the next provider.
      tierTimer = setTimeout(() => {
        if (done || currentTier !== i || adStarted || adsManager) return;
        console.warn(`[DAKID ads] ${label} did not respond in 8s (likely blocked) — trying next provider.`);
        requestTier(i + 1);
      }, 8000);
    } catch {
      requestTier(i + 1);
    }
  };

  // Entry point: start the waterfall at tier 0. Content keeps playing until an
  // ad is actually ready (CONTENT_PAUSE_REQUESTED).
  const requestAd = () => {
    if (!tags.length) return resume();
    done = false;
    adStarted = false;
    contentPaused = false;
    requestTier(0);
  };

  const destroy = () => {
    clearTierTimer();
    clearOverall();
    destroyCurrent();
  };

  return { initOnGesture, requestAd, destroy };
}
